import type { Master } from "@/api/masterService";
import Breadcrumb from "@/components/Breadcrumb";
import BackButton from "@/components/buttons/BackButton";
import MasterTable from "@/components/master/MasterTable";
import SaveMasterModal from "@/components/master/SaveMasterModal";
import {
  useCreateCurrency,
  useGetCurrencies,
  useUpdateCurrency,
} from "@/hooks/exchangeRateHooks";
import { usePermissions } from "@/hooks/usePermissions";
import { useQueryHandler } from "@/hooks/useQueryHandler";
import { DefaultSecondaryButton } from "@/styles/globalstyles";
import { useToast } from "@/hooks/useToast";
import { ErrorAlert } from "@/utils/alert";
import { Add } from "@mui/icons-material";
import { Box, TextField, Button, Typography } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useCurrencyManagementState } from "@/reducers/exchangeRate";
import { getAxiosErrorMessage } from "@/utils/healper";
import type { currencyBody } from "@/api/exchangeRateService";

const ExchangeRateMaster = () => {
  const canEdit = usePermissions().canEdit("exchange.rate");
  const [searchFilter, setSearchFilter] = useState<string>("");
  const { showToast } = useToast();
  const [state, dispatch] = useCurrencyManagementState();

  const {
    data: currencies,
    isFetching: isLoadingCurrencies,
    isError: isErrorCurrencies,
  } = useGetCurrencies();

  const { mutate: createCurrency, isPending: isPendingCreateCurrency } =
    useCreateCurrency();

  const { mutate: updateCurrency, isPending: isPendingUpdateCurrency } =
    useUpdateCurrency();

  useQueryHandler({
    isFetching:
      isLoadingCurrencies || isPendingCreateCurrency || isPendingUpdateCurrency,
    isError: isErrorCurrencies,
  });

  const convertedData: Master[] = useMemo(() => {
    if (!currencies) return [];
    return currencies.map((currency) => ({
      id: currency.id,
      name: currency.currency_name,
      description: currency.description,
      is_active: currency.is_active,
      created_at: currency.created_at,
      updated_at: currency.updated_at,
      created_by: currency.created_by,
      updated_by: currency.updated_by,
    }));
  }, [currencies]);

  const filteredRows = useMemo(() => {
    const sortedData = [...(convertedData ?? [])].sort((a, b) => {
      if (a.is_active === b.is_active) return a.name!.localeCompare(b.name!);
      return a.is_active ? -1 : 1;
    });
    if (!searchFilter) return sortedData;
    return sortedData.filter((row) =>
      row.name?.toLowerCase().includes(searchFilter.toLowerCase()),
    );
  }, [convertedData, searchFilter]);

  useEffect(() => {
    document.title = "Exchange Rate | AMPA";
  }, []);

  const handleClickAdd = () => {
    dispatch({ type: "OPEN_ADD_MODAL" });
  };

  const handleAdd = (master: Master) => {
    const newCurrency: currencyBody = {
      currency_name: master.name!,
      description: master.description!,
      is_active: true,
    };
    createCurrency(newCurrency, {
      onSuccess: () => {
        showToast(`เพิ่ม ${master.name} สำเร็จ`, "success");
        dispatch({ type: "SAVE_SUCCESS" });
      },
      onError: (error) => {
        const message = getAxiosErrorMessage(error);
        ErrorAlert(`Error`, message);
      },
    });
  };

  const handleEdit = (master: Master) => {
    const updatedCurrency: currencyBody = {
      id: master.id!,
      currency_name: master.name!,
      description: master.description!,
      is_active: master.is_active!,
    };
    updateCurrency(updatedCurrency, {
      onSuccess: () => {
        showToast(`แก้ไข ${master.name} สำเร็จ`, "success");
        dispatch({ type: "SAVE_SUCCESS" });
      },
      onError: (error) => {
        const message = getAxiosErrorMessage(error);
        ErrorAlert(`Error`, message);
      },
    });
  };

  const currencyBodyToMaster = (curr: currencyBody): Master => ({
    id: curr.id || 0,
    name: curr.currency_name,
    description: curr.description,
    is_active: curr.is_active || false,
    created_at: "",
    updated_at: "",
    created_by: "",
    updated_by: "",
  });

  return (
    <Box sx={{ px: 3, pt: 3, pb: 1 }}>
      <Breadcrumb
        current="Exchange Rate Master"
        previous={[{ key: "exchange.rate" }]}
      />
      <Box mt={1}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="flex-start"
          gap={2}
        >
          <BackButton to="/exchange-rate" />
          <Typography variant="body1" fontWeight={600}>
            จัดการสกุลเงิน
          </Typography>
        </Box>
        <Box sx={{ p: 1, borderRadius: 1 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box sx={{ flexGrow: 1 }}>
              <TextField
                label="ค้นหา Master"
                variant="outlined"
                size="small"
                value={searchFilter}
                onChange={(e) => setSearchFilter(e.target.value)}
                sx={{
                  mt: 2,
                  mb: 1,
                  width: "30%",
                  "& .MuiInputLabel-root": {
                    fontSize: "0.8rem",
                    color: "#666666",
                    "&.Mui-focused": { color: "primary.main" },
                  },
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": { borderColor: "#cccccc" },
                    "&:hover fieldset": { borderColor: "#999999" },
                  },
                }}
              />
            </Box>
            <Box gap={1} display="flex">
              <Button
                variant="outlined"
                sx={{ ...DefaultSecondaryButton, mt: 2, alignItems: "center" }}
                onClick={handleClickAdd}
                disabled={!canEdit}
                size="small"
              >
                <Add /> เพิ่มสกุลเงิน
              </Button>
            </Box>
          </Box>
          <MasterTable
            data={filteredRows}
            isLoading={false}
            handleEdit={(row) => () => {
              const currency: currencyBody = {
                id: row.id!,
                currency_name: row.name!,
                description: row.description!,
                is_active: row.is_active!,
              };
              dispatch({ type: "OPEN_EDIT_MODAL", payload: currency });
            }}
            canEdit={canEdit}
          />
        </Box>
      </Box>

      <SaveMasterModal
        handleAddMaster={handleAdd}
        handleEditMaster={handleEdit}
        handleClose={() => dispatch({ type: "CLOSE_MODAL" })}
        open={state.modal.isOpen}
        selectedMaster={
          state.modal.selectedItem
            ? currencyBodyToMaster(state.modal.selectedItem)
            : undefined
        }
      />
    </Box>
  );
};

export default ExchangeRateMaster;
